// src/components/AnalysisTabIcons.jsx
//
// Icons for the Design Analysis tab row. Inline SVG like ViewIcons.jsx and
// TraceIcons.jsx — no icon dependency, sized in `em` so they follow the tab's
// font-size, and drawn in `currentColor` so the active tab's colour carries
// through without an extra rule.
//
// Each one draws THE THING THE TAB IS ABOUT, not a generic symbol for the idea
// of it. That is the same rule TraceIcons keeps, and it matters more here
// because six tabs sit side by side: a row of clipboards, gauges and checkmarks
// would be six ways of drawing "analysis" and tell you nothing about which is
// which. So Compliance is a plot with its buildable envelope inside it, and
// Vastu is the nine-square grid with the Brahmasthana filled — the diagram the
// panel actually shows you.
//
// The labels stay. These sit BESIDE the text to make a tab findable at a glance
// once you know it, not to replace the word that teaches it to you.
import React from 'react';

const S = ({ children }) => (
  <svg viewBox="0 0 24 24" width="1.15em" height="1.15em" fill="none"
       stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"
       strokeLinejoin="round" aria-hidden="true" focusable="false"
       style={{ display: 'block', flexShrink: 0 }}>
    {children}
  </svg>
);

/** The shared frame, exported so other icon sets draw in exactly this style. */
export const IconFrame = S;

/** Design — a traced floor plan: an outline divided into rooms. */
export const IconDesign = () => (
  <S>
    <rect x="3" y="4" width="18" height="16" rx="1.5" />
    <path d="M13 4v16M13 12h8" />
  </S>
);

/** Compliance — the plot, with the envelope the setbacks leave inside it. */
export const IconCompliance = () => (
  <S>
    <rect x="2.5" y="3.5" width="19" height="17" rx="1.5" />
    <rect x="6.5" y="7.5" width="11" height="9" rx="1" strokeDasharray="2.4 2" />
  </S>
);

/** Climate — sun over the ground line: what the sun-path and thermal runs read. */
export const IconClimate = () => (
  <S>
    <circle cx="12" cy="9" r="3.6" />
    <path d="M12 1.8v1.6M12 14.6v1.4M4.6 9H3M21 9h-1.6M6.4 3.4l1.1 1.1M17.6 3.4l-1.1 1.1" />
    <path d="M3 20h18" />
  </S>
);

/**
 * Vastu — the nine-square mahapada with the Brahmasthana filled.
 *
 * The centre is drawn as a filled square rather than another outline: the whole
 * reading turns on where the middle of the grid falls, and an outline there
 * would read as a tenth cell instead of the one that matters.
 */
export const IconVastu = () => (
  <S>
    <rect x="3" y="3" width="18" height="18" rx="1.5" />
    <path d="M9 3v18M15 3v18M3 9h18M3 15h18" />
    <rect x="9" y="9" width="6" height="6" fill="currentColor" stroke="none" opacity="0.4" />
  </S>
);

/** Water — a droplet. */
export const IconWater = () => (
  <S>
    <path d="M12 3.2c0 0 5.8 6.1 5.8 9.9a5.8 5.8 0 0 1-11.6 0c0-3.8 5.8-9.9 5.8-9.9z" />
  </S>
);

/** Electricity — a bolt. */
export const IconElectricity = () => (
  <S>
    <path d="M13.2 2.5 5.8 13.4h5.2l-0.9 8.1 7.4-11.4h-5.2l0.9-7.6z" />
  </S>
);

/* ---- the Climate Analysis menu ------------------------------------------------ */

/** Sun Path — the sun partway along its arc over the ground: the path it is named for. */
export const IconSunPath = () => (
  <S>
    <path d="M3 19h18" />
    <path d="M4.5 19a7.5 7.5 0 0 1 15 0" strokeDasharray="2 2.2" />
    <circle cx="15.75" cy="12.5" r="2.3" fill="currentColor" stroke="none" />
  </S>
);

/** Wind Path — streamlines, two curling off where the flow is turned. */
export const IconWindPath = () => (
  <S>
    <path d="M3 8h10.5a2.5 2.5 0 1 0-2.5-2.5" />
    <path d="M3 12h15a3 3 0 1 1-3 3" />
    <path d="M3 16h7" />
  </S>
);

/** Temperature — a thermometer with its scale. */
export const IconTemperature = () => (
  <S>
    <path d="M14 14.8V5a2 2 0 0 0-4 0v9.8a4 4 0 1 0 4 0z" />
    <path d="M12 9.5v6.5M17 5h2.5M17 8.5h2.5" />
  </S>
);

/** Keyed by ClimateAnalysisPanel's TOOLS keys. */
export const CLIMATE_TOOL_ICON = {
  sun: IconSunPath,
  wind: IconWindPath,
  temperature: IconTemperature,
  water: IconWater,
  power: IconElectricity,
};

/** Keyed by the tab's own `key`, so the row stays a single map over TABS. */
export const TAB_ICON = {
  design: IconDesign,
  compliance: IconCompliance,
  climate: IconClimate,
  vastu: IconVastu,
  water: IconWater,
  electricity: IconElectricity,
};
